import {Component, HostBinding, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router} from '@angular/router';
import {Lib} from "../libs/Lib";
import {OverlayContainer} from '@angular/cdk/overlay';
import {LocalStoreService} from './services';
import {themeColor} from './common/theme-color';
import {AppService} from "./services";
import {isGalaxy, isSkipLoadingDataRoute} from "./common";
import {chunkReloadAlreadyTried, clearChunkReloadFlag, isChunkLoadError, markChunkReloadTried} from "./common/chunk-load-error";
import {AppBusyService} from "./services/app-busy.service";

/**
 * Don't flash the navigation loader for quick moves - only show it once a navigation has taken
 * long enough that the user would otherwise think the app had frozen.
 */
const NAV_LOADER_DELAY_MS = 180;

/**
 * Pacing of the secondary status line.
 *
 * Each line lingers a little longer than the one before it, so a short sequence does not sprint
 * through everything it has to say in five seconds and then sit frozen on its last word for the
 * remaining twenty. The wait feels slowest at the end, so that is where the lines are slowest too.
 */
const NAV_STAGE_MS = 1100;
const NAV_STAGE_GROWTH_MS = 450;

/** how often the line is re-evaluated; cheap, it is a string comparison */
const NAV_STAGE_TICK_MS = 400;

/**
 * Once a destination has run out of steps AND the wait has gone on this long, stop pretending
 * there is another phase to narrate and say what is actually true. Inventing more steps here
 * would be the fake-progress bar problem in words.
 */
const NAV_SLOW_NOTICE_MS = 22000;
const NAV_SLOW_TEXT = 'still downloading, this can take a minute';

/**
 * Secondary lines used when a destination does not name its own. Every sequence starts with the
 * bundle because that is always what the first seconds are spent on.
 */
const NAV_STAGES_DEFAULT = ['downloading bundle', 'initializing bundle', 'preparing components', 'setting things up'];

/**
 * Appended to every destination's own steps. The end of a long wait is where the screen feels
 * most stuck, so "almost ready" is the last of five lines rather than a single word the panel
 * sits on for twenty seconds. Nothing below names a destination, so it suits any of them.
 */
const NAV_STAGES_TAIL = [
    'unpacking components',
    'initializing modules',
    'connecting services',
    'building interface',
    'almost ready',
];

/**
 * Where the user is going, matched against the URL being navigated to.
 *
 * The label is real information - the router already knows the destination - so it beats a generic
 * "loading" and tells the user their click registered on the thing they clicked. The stages under
 * it then name what that particular page is bringing up, advanced on a timer, which is what makes
 * a long wait read as progress instead of a freeze.
 */
interface INavDestination {
    /** url prefix, matched from the start of the route being navigated to */
    path: string;
    /** primary line, e.g. "opening editor" */
    label: string;
    /** secondary lines for this destination; falls back to NAV_STAGES_DEFAULT */
    stages?: string[];
}

/** first match wins, so longer paths come before the shorter ones they start with */
const NAV_DESTINATIONS: INavDestination[] = [
    {
        path: '/shopping-cart/equipment', label: 'opening equipment',
        stages: ['downloading bundle', 'initializing bundle', 'preparing equipment forms', 'loading vehicle types'],
    },
    {
        path: '/getstarted', label: 'getting started',
        stages: ['downloading bundle', 'initializing bundle', 'loading design library', 'creating templates',
            'preparing designs', 'loading previews', 'setting up your workspace'],
    },
    {
        path: '/shopping-cart', label: 'opening store',
        stages: ['downloading bundle', 'initializing bundle', 'loading products', 'loading prices', 'preparing checkout'],
    },
    {
        path: '/shopping', label: 'opening store',
        stages: ['downloading bundle', 'initializing bundle', 'loading products', 'loading prices', 'preparing checkout'],
    },
    {
        path: '/editor', label: 'opening editor',
        stages: ['downloading bundle', 'initializing bundle', 'loading scene engine', 'loading components',
            'preparing canvas', 'preparing dynamic ai'],
    },
    {
        path: '/resources', label: 'opening resources',
        stages: ['downloading bundle', 'initializing bundle', 'loading media library', 'building thumbnails'],
    },
    {
        path: '/stations', label: 'opening players',
        stages: ['downloading bundle', 'initializing bundle', 'loading players', 'checking player status'],
    },
    {
        path: '/scheduler', label: 'opening scheduler',
        stages: ['downloading bundle', 'initializing bundle', 'loading schedules', 'preparing timeline'],
    },
    {
        path: '/catalog', label: 'opening catalog',
        stages: ['downloading bundle', 'initializing bundle', 'loading catalog', 'loading previews'],
    },
    {path: '/events', label: 'opening events'},
    {path: '/enterprise', label: 'opening enterprise'},
    {path: '/adnet', label: 'opening adnet'},
    {path: '/dynawebs', label: 'opening dynawebs'},
    {path: '/transitions', label: 'opening transitions'},
    {path: '/fasterq', label: 'opening fasterq'},
    {
        path: '/statistics', label: 'opening statistics',
        stages: ['downloading bundle', 'initializing bundle', 'loading reports', 'building charts'],
    },
    {path: '/monitor', label: 'opening monitor'},
    {path: '/prices', label: 'opening plans'},
    {path: '/orders', label: 'opening orders'},
    {path: '/paint', label: 'opening paint'},
    {path: '/music', label: 'opening music'},
    {path: '/login', label: 'opening sign in'},
    {path: '/migrate', label: 'preparing migration'},
    {path: '/user', label: 'opening profile'},
    {path: '/help', label: 'opening help'},
    {
        path: '/home', label: 'opening dashboard',
        stages: ['downloading bundle', 'initializing bundle', 'processing account', 'loading your screens',
            'preparing dashboard'],
    },
];

/** used when the destination is not one we have a name for */
const NAV_DESTINATION_FALLBACK: INavDestination = {path: '', label: 'loading'};

/**
 * Shown when the loader is up for a lazily loaded dialog rather than a route change. A dialog has
 * no destination URL, so naming one would be a lie: before this, opening the upgrade wizard from
 * inside the editor announced itself as "opening editor" with the editor's own stage wording.
 */
const NAV_DIALOG_LABEL = 'loading';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnDestroy {
    // name = environment.appName;
    // title: string;
    private subscription;
    private navSubscription;
    private busySubscription;
    private navLoaderTimer: any = null;
    /** a router navigation is in flight (may be downloading a lazy chunk) */
    private pendingNav = false;
    /** a tracked dynamic import is in flight (e.g. opening a lazily loaded dialog) */
    private pendingBusy = false;
    /** true once the wait has lasted long enough to be worth showing a loader for */
    navigating = false;
    /** what the loader says it is opening, e.g. "opening editor" */
    navLabel = 'loading';
    /** where the current ROUTE navigation is heading; only shown while one is actually in flight */
    private routeLabel = NAV_DIALOG_LABEL;
    private routeStages: string[] = NAV_STAGES_DEFAULT;
    /** secondary line that advances while the wait continues */
    navStage = NAV_STAGES_DEFAULT[0];
    /** the sequence the secondary line is walking, chosen by destination */
    private navStages: string[] = NAV_STAGES_DEFAULT;
    private navStageTimer: any = null;
    /** when the current loader appeared, so the wording is paced by the clock, not by tick count */
    private navStartedAt = 0;
    @HostBinding('class') componentCssClass = 'app-theme-dark';

    constructor(private appService: AppService, private localStoreService: LocalStoreService, public overlayContainer: OverlayContainer,
                private router: Router, private busyService: AppBusyService, private cdr: ChangeDetectorRef) {

        // Feature code is split into lazy chunks, so a click can mean "download a chunk first".
        // Without feedback the app looks frozen. Registered before the auth-callback bail-out
        // below so it is always active, and covers BOTH sources of waiting:
        //   1. router navigations             -> router events
        //   2. dynamic import() from anywhere -> AppBusyService
        this.navSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.pendingNav = true;
                const destination = this.destinationForUrl(event.url);
                this.routeLabel = destination.label;
                this.routeStages = destination.stages || NAV_STAGES_DEFAULT;
                this.updateLoader();
                // NavigationSkipped is included below on purpose: it is a terminal event that can
                // follow NavigationStart, and if it were not handled the loader would stay on
                // screen forever - a worse "frozen app" than the one it exists to prevent.
            } else if (event instanceof NavigationEnd || event instanceof NavigationCancel
                || event instanceof NavigationError || event instanceof NavigationSkipped) {
                this.pendingNav = false;
                this.updateLoader();
                if (event instanceof NavigationError) {
                    this.handleFailedNavigation(event);
                } else if (event instanceof NavigationEnd) {
                    // a navigation completed, so lazy chunks are loading fine again - give the tab
                    // back its one retry, otherwise a SECOND deploy in a long session is a dead click
                    clearChunkReloadFlag();
                }
            }
        });
        this.busySubscription = this.busyService.busy$.subscribe((busy) => {
            this.pendingBusy = busy;
            this.updateLoader();
        });

        // Skip all initialization for auth-callback route
        if (isSkipLoadingDataRoute()) {
            return;
        }

        console.log('> DevMode ' + Lib.DevMode());
        console.log('> Galaxy ' + isGalaxy());
        // const zoom = "100%";
        // document.body.style.zoom = zoom;
        // console.log('app zoom ' + zoom);
        // router.events
        //   .filter((event) => event instanceof NavigationEnd)
        //   .map(() => this.route.firstChild)
        //   .filter((childRoute) => childRoute !== null)
        //   .switchMap((childRoute) => childRoute.data)
        //   .filter((data) => !!data)
        //   .subscribe((data: Data) => {
        //     this.title = data.title;
        //   });
        this.subscription = this.localStoreService.theme.subscribe((val) => {
            const theme = this.localStoreService.getStorageValConfig('theme');
            this.convertTheme(theme);
        });
    }

    convertTheme(theme) {
        // convert angular material theme
        let colors;

        // Remove all theme classes first
        document.body.classList.remove('app-theme-dark', 'app-theme-light', 'app-theme-gradient');

        if (theme == '1') {
            document.body.classList.add('app-theme-light');
            this.componentCssClass = 'app-theme-light';
            colors = themeColor['app-theme-light'];
        } else if (theme == '2') {
            document.body.classList.add('app-theme-gradient');
            this.componentCssClass = 'app-theme-gradient';
            colors = themeColor['app-theme-gradient'];
        } else {
            document.body.classList.add('app-theme-dark');
            this.componentCssClass = 'app-theme-dark';
            colors = themeColor['app-theme-dark'];
        }

        // convert custom colors
        Object.keys(colors).forEach(property =>
            document.documentElement.style.setProperty(property, colors[property])
        );
    }

    /**
     * A navigation can fail because the lazy chunk it needs is no longer on the server - which is
     * what every deploy does to tabs that are already open. Left alone the click does nothing and
     * the app looks broken, so fetch the page fresh instead. Only once per tab, so a chunk that is
     * genuinely broken can never cause a reload loop.
     */
    private handleFailedNavigation(event: NavigationError) {
        if (!isChunkLoadError(event.error)) {
            return;
        }
        if (chunkReloadAlreadyTried()) {
            console.error('lazy chunk still missing after a reload, giving up', event.error);
            return;
        }
        markChunkReloadTried();
        // event.url is app-internal ("/getstarted/template") and does NOT include the deployment
        // base href ("/signstudio/"), so resolve it against document.baseURI - assigning the raw
        // router url would 404 in production.
        const target = new URL(String(event.url || '').replace(/^\/+/, ''), document.baseURI).toString();
        console.warn('lazy chunk missing (app was most likely redeployed) - reloading to', target);
        // full page load of the URL the user asked for: picks up the new index.html and chunks,
        // and lands them where they were trying to go instead of dumping them back at the start
        window.location.assign(target);
    }

    private clearNavLoaderTimer() {
        if (this.navLoaderTimer) {
            clearTimeout(this.navLoaderTimer);
            this.navLoaderTimer = null;
        }
    }

    /**
     * Shows the loader only once the wait has lasted NAV_LOADER_DELAY_MS, so an already downloaded
     * (cached) chunk resolves first and the user never sees a flash.
     */
    private updateLoader() {
        this.applyLoaderText();
        if (this.pendingNav || this.pendingBusy) {
            if (this.navigating || this.navLoaderTimer) {
                return;
            }
            this.navLoaderTimer = setTimeout(() => {
                this.navLoaderTimer = null;
                this.setNavigating(true);
            }, NAV_LOADER_DELAY_MS);
            return;
        }
        this.clearNavLoaderTimer();
        this.setNavigating(false);
    }

    /**
     * Decides what the panel says. A route navigation names its destination, because the router
     * knows it. A lazily loaded dialog has no destination, so it gets neutral wording instead of
     * whatever route happened to be opened last.
     */
    private applyLoaderText() {
        this.navLabel = this.pendingNav ? this.routeLabel : NAV_DIALOG_LABEL;
        this.navStages = this.pendingNav ? this.routeStages : NAV_STAGES_DEFAULT;
    }

    /** picks the name and the stage wording for the route being navigated to */
    private destinationForUrl(url: string): INavDestination {
        const path = String(url || '').split('?')[0].toLowerCase();
        for (const destination of NAV_DESTINATIONS) {
            if (path.indexOf(destination.path) === 0) {
                return destination;
            }
        }
        return NAV_DESTINATION_FALLBACK;
    }

    /**
     * Walks the secondary status line forward while the wait continues, so the panel never sits on
     * one word. It stops on the last entry rather than looping: cycling forever would suggest the
     * app is retrying in circles.
     */
    private startNavStages() {
        this.stopNavStages();
        this.navStartedAt = Date.now();
        this.navStage = this.stageForElapsed(0, this.stageList());
        // Driven by elapsed time rather than by counting ticks, so the list is re-read every tick
        // (a second navigation starting mid-wait switches to its own wording) and the line can
        // never advance past what the clock says.
        this.navStageTimer = setInterval(() => {
            const next = this.stageForElapsed(Date.now() - this.navStartedAt, this.stageList());
            if (next === this.navStage) {
                return;
            }
            this.navStage = next;
            this.cdr.detectChanges();
        }, NAV_STAGE_TICK_MS);
    }

    /**
     * Which line belongs on screen after `elapsed` ms. Each step lasts longer than the one before
     * it, so a sequence stretches across a slow download instead of racing to the end. Once the
     * steps are used up the last one holds, and on a genuinely long wait it gives way to a plain
     * statement of what is happening rather than an invented extra phase.
     */
    private stageForElapsed(elapsed: number, stages: string[]): string {
        let until = 0;
        for (let i = 0; i < stages.length; i++) {
            until += NAV_STAGE_MS + (i * NAV_STAGE_GROWTH_MS);
            if (elapsed < until) {
                return stages[i];
            }
        }
        return elapsed >= NAV_SLOW_NOTICE_MS ? NAV_SLOW_TEXT : stages[stages.length - 1];
    }

    /** the destination's own steps followed by the shared tail; never empty */
    private stageList(): string[] {
        const own = this.navStages && this.navStages.length ? this.navStages : NAV_STAGES_DEFAULT;
        return own.concat(NAV_STAGES_TAIL);
    }

    private stopNavStages() {
        if (this.navStageTimer) {
            clearInterval(this.navStageTimer);
            this.navStageTimer = null;
        }
    }

    private setNavigating(value: boolean) {
        if (this.navigating === value) {
            return;
        }
        this.navigating = value;
        if (value) {
            this.startNavStages();
        } else {
            this.stopNavStages();
        }
        // the flag flips from a timer / router callback, so paint it explicitly
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.navSubscription) {
            this.navSubscription.unsubscribe();
        }
        if (this.busySubscription) {
            this.busySubscription.unsubscribe();
        }
        this.clearNavLoaderTimer();
        this.stopNavStages();
    }
}
